import { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { fetchTasksAsync, deleteTaskAsync, createTaskAsync } from '../slice/taskSlice';
import { duplicateTask } from '../Api/taskApi';
import { fetchAllOrganizations } from '../slice/organizationsSlice';
import axios from 'axios';
import { BASE_URL } from '../components/constants';
import { useThemeColor } from '../hooks/useThemeColor';
import { useToast } from '../components/Toast';
import { parseISO, format } from 'date-fns';
import { useTranslation } from '../i18n/LanguageContext';
import { authHeader } from '../utils/authHeaders';
import {
  Search,
  Plus,
  MoreHorizontal,
  LayoutGrid,
  List,
  Settings2,
  Trash2,
  Copy,
  X,
  ChevronDown,
  Check,
  Loader2,
  BrainCircuit
} from 'lucide-react';

// Agent Icon Component — solid app theme color with sidebar's agent icon
const AgentIcon = () => {
  const { aispaceColor } = useThemeColor();
  return (
    <div
      className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
      style={{ backgroundColor: `${aispaceColor}20` }}
    >
      <BrainCircuit className="w-5 h-5" strokeWidth={2} style={{ color: aispaceColor }} />
    </div>
  );
};

// Agent Image component - shows uploaded image or falls back to default
const AgentImage = ({ agentId, agentName, imagePath, type = 'icon' }) => {
  const [imgError, setImgError] = useState(false);
  const showImage = imagePath && !imgError;
  const imgSrc = imagePath ? `${BASE_URL}/agents/media/${agentId}` : '';

  if (type === 'banner') {
    return (
      <div className="w-full h-40">
        {showImage ? (
          <img
            src={imgSrc}
            alt={agentName}
            className="w-full h-full object-cover"
            style={{ display: showImage ? 'block' : 'none' }}
            onError={() => setImgError(true)}
          />
        ) : (
          <AgentBanner />
        )}
      </div>
    );
  }

  return showImage ? (
    <img
      src={imgSrc}
      alt={agentName}
      className="w-10 h-10 rounded-full object-cover flex-shrink-0"
      onError={() => setImgError(true)}
    />
  ) : (
    <AgentIcon />
  );
};

// Agent Card Banner for Grid View — solid app theme color with BrainCircuit
const AgentBanner = () => {
  const { aispaceColor } = useThemeColor();
  return (
    <div
      className="w-full h-40 flex items-center justify-center overflow-hidden"
      style={{ backgroundColor: `${aispaceColor}20` }}
    >
      <BrainCircuit className="w-20 h-20" strokeWidth={1.75} style={{ color: aispaceColor }} />
    </div>
  );
};

const Agents = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { aispaceColor } = useThemeColor();
  const { addToast, ToastContainer } = useToast();

  const { tasks = [], status } = useSelector((state) => state.tasks);
  const { organizations = [], error: organizationsError } = useSelector((state) => state.organizations);
  const { data: userData } = useSelector((state) => state.admin);

  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [viewMode, setViewMode] = useState('list');
  const [openDropdown, setOpenDropdown] = useState(null);
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, agent: null, isBulk: false });
  const [showHeaderDropdown, setShowHeaderDropdown] = useState(false);
  const [selectedAgents, setSelectedAgents] = useState(new Set());
  const [selectionModeActive, setSelectionModeActive] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [duplicatingId, setDuplicatingId] = useState(null);
  const [createAgentModal, setCreateAgentModal] = useState(false);
  const [newAgent, setNewAgent] = useState({
    name: '',
    description: '',
    organization_id: '',
    department_id: '',
    image: null,
    imageFile: null
  });
  const [orgDepartments, setOrgDepartments] = useState([]);
  const [isCreatingAgent, setIsCreatingAgent] = useState(false);
  const [orgDropdownOpen, setOrgDropdownOpen] = useState(false);
  const [deptDropdownOpen, setDeptDropdownOpen] = useState(false);
  const orgDropdownRef = useRef(null);
  const deptDropdownRef = useRef(null);

  // Filter modal state
  const [filterOpen, setFilterOpen] = useState(false);
  const [appliedOrgs, setAppliedOrgs] = useState([]);
  const [appliedUserGroups, setAppliedUserGroups] = useState([]);
  const [appliedDate, setAppliedDate] = useState(null);
  const [appliedMonth, setAppliedMonth] = useState('');
  const [appliedSort, setAppliedSort] = useState('newest');
  const [draftOrgs, setDraftOrgs] = useState([]);
  const [draftUserGroups, setDraftUserGroups] = useState([]);
  const [draftDate, setDraftDate] = useState(null);
  const [draftMonth, setDraftMonth] = useState('');
  const [draftSort, setDraftSort] = useState('newest');
  const [orgDdOpen, setOrgDdOpen] = useState(false);
  const [ugDdOpen, setUgDdOpen] = useState(false);
  const [monthDdOpen, setMonthDdOpen] = useState(false);
  const [sortDdOpen, setSortDdOpen] = useState(false);
  // Departments fetched per selected org(s) — drives the User Group options
  // when organisations are selected.
  const [orgDeptOptions, setOrgDeptOptions] = useState([]);
  // ADMIN 2: all department names for this admin's org (or all orgs for SUPER_ADMIN)
  // used as the fallback user-group list when no org filter is selected.
  const [allOrgDeptNames, setAllOrgDeptNames] = useState([]);
  const orgDdRef = useRef(null);
  const ugDdRef = useRef(null);
  const monthDdRef = useRef(null);
  const sortDdRef = useRef(null);

  const dropdownRef = useRef(null);
  const agentImageInputRef = useRef(null);
  const headerDropdownRef = useRef(null);
  const email = localStorage.getItem('email');

  const isSuperAdmin = userData?.user_role === 'SUPER_ADMIN';
  const isAdmin = userData?.user_role === 'ADMIN';
  const userOrganizationId = userData?.organization_id;

  // ITEM 3: ADMIN sees only their own org in Create Agent modal.
  // SUPER_ADMIN sees all organizations.
  const creatableOrganizations = isSuperAdmin
    ? organizations
    : organizations.filter(o => o.organization_id === userOrganizationId);

  // Items per page based on view mode
  const itemsPerPageGrid = 6;
  const itemsPerPageList = 10;
  const itemsPerPage = viewMode === 'grid' ? itemsPerPageGrid : itemsPerPageList;

  // Fetch tasks and organizations
  useEffect(() => {
    if (email) {
      if (isSuperAdmin) {
        dispatch(fetchTasksAsync());
      } else {
        dispatch(fetchTasksAsync(email));
      }
      dispatch(fetchAllOrganizations());
    }
  }, [dispatch, email, isSuperAdmin]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpenDropdown(null);
      }
      if (headerDropdownRef.current && !headerDropdownRef.current.contains(event.target)) {
        setShowHeaderDropdown(false);
      }
      if (orgDropdownRef.current && !orgDropdownRef.current.contains(event.target)) {
        setOrgDropdownOpen(false);
      }
      if (deptDropdownRef.current && !deptDropdownRef.current.contains(event.target)) {
        setDeptDropdownOpen(false);
      }
      if (orgDdRef.current && !orgDdRef.current.contains(event.target)) {
        setOrgDdOpen(false);
      }
      if (ugDdRef.current && !ugDdRef.current.contains(event.target)) {
        setUgDdOpen(false);
      }
      if (monthDdRef.current && !monthDdRef.current.contains(event.target)) {
        setMonthDdOpen(false);
      }
      if (sortDdRef.current && !sortDdRef.current.contains(event.target)) {
        setSortDdOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Clear selections and disable selection mode when page or view changes
  useEffect(() => {
    setSelectedAgents(new Set());
    setSelectionModeActive(false);
  }, [currentPage, viewMode]);

  // ADMIN 2: fetch all departments so the User Group filter shows every department,
  // not just those that already have agents.
  // Admin → only their own org. Super-Admin → all organizations.
  useEffect(() => {
    if (!organizations || organizations.length === 0) return;
    if (!isAdmin && !isSuperAdmin) return;
    const orgsToFetch = isSuperAdmin
      ? organizations
      : organizations.filter(o => o.organization_id === userOrganizationId);
    if (orgsToFetch.length === 0) return;
    let cancelled = false;
    Promise.all(
      orgsToFetch.map((org) =>
        axios
          .get(`${BASE_URL}/departments/${org.organization_id}`, { headers: authHeader() })
          .then((res) => res.data?.departments || [])
          .catch(() => []),
      ),
    ).then((perOrg) => {
      if (cancelled) return;
      const seen = new Set();
      const names = [];
      perOrg.flat().forEach((d) => {
        if (d.department_name && !seen.has(d.department_name)) {
          seen.add(d.department_name);
          names.push(d.department_name);
        }
      });
      setAllOrgDeptNames(names);
    });
    return () => { cancelled = true; };
  }, [organizations, isSuperAdmin, userOrganizationId]);

  // Filter modal: when orgs are selected, fetch departments belonging to those
  // orgs to drive the User Group options (org-dependent dropdown).
  useEffect(() => {
    if (draftOrgs.length === 0) {
      setOrgDeptOptions([]);
      return;
    }
    let cancelled = false;
    Promise.all(
      draftOrgs.map((orgId) =>
        axios
          .get(`${BASE_URL}/departments/${orgId}`, { headers: authHeader() })
          .then((res) => res.data?.departments || [])
          .catch(() => []),
      ),
    ).then((perOrg) => {
      if (cancelled) return;
      const seen = new Set();
      const opts = [];
      perOrg.flat().forEach((d) => {
        const name = d.department_name;
        if (name && !seen.has(name)) {
          seen.add(name);
          opts.push(name);
        }
      });
      setOrgDeptOptions(opts);
      // Drop any draft user groups that are no longer in the active option set.
      setDraftUserGroups((prev) => prev.filter((c) => opts.includes(c)));
    });
    return () => { cancelled = true; };
  }, [draftOrgs]);

  // Filter agents based on user role and exclude task ID 40 / internal tasks
  const filteredAgents = tasks
    .filter(task => {
      // Exclude task with ID 40
      if (task.id === 40) return false;
      // Exclude internal chat task
      if (task.name === '__CHAT_INTERNAL__') return false;

      if (isSuperAdmin) {
        return true;
      }
      // ADMIN: only their organisation's tasks (+ global tasks with no org)
      return task.organization_id === userOrganizationId || !task.organization_id;
    })
    .filter(task => {
      const name = task.name || '';
      const orgName = getOrganizationName(task.organization_id) || '';
      return name.toLowerCase().includes(searchQuery.toLowerCase()) ||
             orgName.toLowerCase().includes(searchQuery.toLowerCase());
    })
    .filter(task => {
      // Organisation filter — applied first so the User Group options below
      // are scoped to the selected organisations (per design ask).
      if (appliedOrgs.length === 0) return true;
      return appliedOrgs.includes(task.organization_id);
    })
    .filter(task => {
      // User Group filter
      if (appliedUserGroups.length === 0) return true;
      return appliedUserGroups.includes(task.department_name || '');
    })
    .filter(task => {
      // Last Update range filter
      if (!appliedDate) return true;
      const ts = task.CreatedAt;
      if (!ts) return false;
      try {
        const d = parseISO(ts);
        const now = new Date();
        const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        if (appliedDate === 'today') return d >= startOfToday;
        if (appliedDate === '7days') {
          const cutoff = new Date(startOfToday);
          cutoff.setDate(cutoff.getDate() - 6);
          return d >= cutoff;
        }
        if (appliedDate === '30days') {
          const cutoff = new Date(startOfToday);
          cutoff.setDate(cutoff.getDate() - 29);
          return d >= cutoff;
        }
        return true;
      } catch {
        return false;
      }
    })
    .filter(task => {
      // Month filter
      if (appliedMonth === '') return true;
      const ts = task.CreatedAt;
      if (!ts) return false;
      try {
        return parseISO(ts).getMonth() === Number(appliedMonth);
      } catch {
        return false;
      }
    })
    .slice()
    .sort((a, b) => {
      const ta = a.CreatedAt ? new Date(a.CreatedAt).getTime() : 0;
      const tb = b.CreatedAt ? new Date(b.CreatedAt).getTime() : 0;
      switch (appliedSort) {
        case 'oldest': return ta - tb;
        case 'mostProgress': return (b.id || 0) - (a.id || 0);
        case 'leastProgress': return (a.id || 0) - (b.id || 0);
        case 'newest':
        default: return tb - ta;
      }
    });

  // User-group fallback when no organisations are selected — derived from the
  // tasks the user can already see. When orgs ARE selected, the dropdown uses
  // `orgDeptOptions` (fetched from the backend) instead.
  const availableUserGroups = Array.from(
    new Set(
      tasks
        .filter(t => t.id !== 40)
        .map(t => t.department_name)
        .filter(Boolean)
    )
  );

  // Active filter count for badge
  const activeFilterCount =
    (appliedOrgs.length > 0 ? 1 : 0) +
    (appliedUserGroups.length > 0 ? 1 : 0) +
    (appliedDate ? 1 : 0) +
    (appliedMonth !== '' ? 1 : 0) +
    (appliedSort !== 'newest' ? 1 : 0);

  const openFilterModal = () => {
    setDraftOrgs(appliedOrgs);
    setDraftUserGroups(appliedUserGroups);
    setDraftDate(appliedDate);
    setDraftMonth(appliedMonth);
    setDraftSort(appliedSort);
    setOrgDdOpen(false);
    setUgDdOpen(false);
    setMonthDdOpen(false);
    setSortDdOpen(false);
    setFilterOpen(true);
  };

  const applyFilters = () => {
    setAppliedOrgs(draftOrgs);
    setAppliedUserGroups(draftUserGroups);
    setAppliedDate(draftDate);
    setAppliedMonth(draftMonth);
    setAppliedSort(draftSort);
    setCurrentPage(1);
    setFilterOpen(false);
  };

  const clearAllFilters = () => {
    setDraftOrgs([]);
    setDraftUserGroups([]);
    setDraftDate(null);
    setDraftMonth('');
    setDraftSort('newest');
    setAppliedOrgs([]);
    setAppliedUserGroups([]);
    setAppliedDate(null);
    setAppliedMonth('');
    setAppliedSort('newest');
    setCurrentPage(1);
  };

  // Get organization name by ID
  function getOrganizationName(orgId) {
    if (!orgId) return 'Global';
    const org = organizations.find(o => o.organization_id === orgId);
    return org?.organization_name || 'Unbekannt';
  }

  // Format date to match Figma (e.g., "Jan 4, 2025")
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '-';
    try {
      const date = parseISO(timestamp);
      return format(date, 'MMM d, yyyy');
    } catch {
      return '-';
    }
  };

  // Pagination
  const totalPages = Math.ceil(filteredAgents.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedAgents = filteredAgents.slice(startIndex, startIndex + itemsPerPage);

  // Handle delete click
  const handleDeleteClick = (agent) => {
    setDeleteModal({ isOpen: true, agent });
    setOpenDropdown(null);
  };

  // Confirm delete
  const confirmDelete = async () => {
    if (!deleteModal.agent) return;
    try {
      setDeletingId(deleteModal.agent.id);
      await dispatch(deleteTaskAsync(deleteModal.agent.id)).unwrap();
      setDeleteModal({ isOpen: false, agent: null, isBulk: false });
      addToast(t('agents.toastDeleted'), 'success');
      // Adjust page if current page becomes empty
      const remainingItems = filteredAgents.length - 1;
      const maxPage = Math.max(1, Math.ceil(remainingItems / itemsPerPage));
      if (currentPage > maxPage) setCurrentPage(maxPage);
    } catch (error) {
      console.error('Failed to delete agent:', error);
      addToast(t('agents.toastDeleteError'), 'error');
    } finally {
      setDeletingId(null);
    }
  };

  // Cancel delete
  const cancelDelete = () => {
    setDeleteModal({ isOpen: false, agent: null, isBulk: false });
  };

  // Handle bulk delete click
  const handleBulkDeleteClick = () => {
    setDeleteModal({ isOpen: true, agent: null, isBulk: true });
  };

  // Confirm bulk delete
  const confirmBulkDelete = async () => {
    const count = selectedAgents.size;
    try {
      setDeletingId('bulk');
      const deletePromises = Array.from(selectedAgents).map(id => dispatch(deleteTaskAsync(id)).unwrap());
      await Promise.all(deletePromises);
      setSelectedAgents(new Set());
      setSelectionModeActive(false);
      setDeleteModal({ isOpen: false, agent: null, isBulk: false });
      addToast(count > 1 ? t('agents.toastBulkDeleted', { count }) : t('agents.toastDeleted'), 'success');
      // Adjust page if current page becomes empty
      const remainingItems = filteredAgents.length - count;
      const maxPage = Math.max(1, Math.ceil(remainingItems / itemsPerPage));
      if (currentPage > maxPage) setCurrentPage(maxPage);
    } catch (error) {
      console.error('Failed to delete agents:', error);
      addToast(t('agents.toastBulkDeleteError'), 'error');
    } finally {
      setDeletingId(null);
    }
  };

  // Handle settings click (navigate to agent settings page)
  const handleSettingsClick = (agentId) => {
    navigate(`/agents/settings/${agentId}`);
    setOpenDropdown(null);
  };

  // Handle create agent - open modal
  const handleCreateAgent = () => {
    if (isSuperAdmin || isAdmin) {
      setNewAgent({
        name: '',
        description: '',
        organization_id: '',
        department_id: '',
        image: null,
        imageFile: null
      });
      setCreateAgentModal(true);
    } else {
      addToast(t('agents.toastCreateNoPermission'), 'error');
    }
  };

  // Close create agent modal
  const closeCreateAgentModal = () => {
    setCreateAgentModal(false);
    setOrgDepartments([]);
    setNewAgent({
      name: '',
      organization_id: '',
      department_id: '',
      image: null,
      imageFile: null
    });
  };

  // Handle agent image upload
  const handleAgentImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setNewAgent(prev => ({ ...prev, image: reader.result, imageFile: file }));
      };
      reader.readAsDataURL(file);
    }
  };

  // Handle add agent submit
  const handleAddAgent = async () => {
    if (!newAgent.name.trim()) {
      addToast(t('agents.createModalNameRequired'), 'error');
      return;
    }

    try {
      setIsCreatingAgent(true);

      // Create task data for API
      const taskData = {
        name: newAgent.name,
        description: newAgent.description || null,
        organization_id: newAgent.organization_id || null,
        department_id: newAgent.department_id || null
      };

      // Call the API to create agent/task
      const result = await dispatch(createTaskAsync(taskData)).unwrap();
      const createdTaskId = result?.task_id || result?.id || result?.taskId;

      // Upload image if selected
      if (newAgent.imageFile && createdTaskId) {
        try {
          const formData = new FormData();
          formData.append('file', newAgent.imageFile);
          formData.append('task_id', String(createdTaskId));
          await axios.post(`${BASE_URL}/agents/upload-image`, formData, {
            headers: { ...authHeader() },
          });
        } catch (imgErr) {
          console.error('Failed to upload agent image:', imgErr);
        }
      }

      closeCreateAgentModal();

      // Refresh the agents list first, then show toast
      if (isSuperAdmin) {
        await dispatch(fetchTasksAsync()).unwrap();
      } else {
        await dispatch(fetchTasksAsync(email)).unwrap();
      }
      addToast(t('agents.toastCreated', { name: newAgent.name }), 'success');
    } catch (error) {
      console.error('Failed to create agent:', error);
      addToast(error?.message || t('agents.toastCreateError'), 'error');
    } finally {
      setIsCreatingAgent(false);
    }
  };

  // Toggle select all for current page
  const toggleSelectAll = () => {
    if (selectionModeActive) {
      // Disable selection mode and clear all selections
      setSelectionModeActive(false);
      setSelectedAgents(new Set());
    } else {
      // Enable selection mode and select all on current page
      setSelectionModeActive(true);
      const currentPageIds = paginatedAgents.map(a => a.id);
      setSelectedAgents(new Set(currentPageIds));
    }
    setShowHeaderDropdown(false);
  };

  // Toggle individual agent selection
  const toggleAgentSelection = (agentId) => {
    const newSelected = new Set(selectedAgents);
    if (newSelected.has(agentId)) {
      newSelected.delete(agentId);
    } else {
      newSelected.add(agentId);
    }
    setSelectedAgents(newSelected);
  };

  // Check if all agents on current page are selected
  const allCurrentPageSelected = paginatedAgents.length > 0 &&
    paginatedAgents.every(a => selectedAgents.has(a.id));

  // Handle duplicate agent — clones General Settings, AI Profile, Standard
  // Questions (with categories) and Shortcuts via the backend. Shows a spinner
  // on the row's three-dots button while in flight and waits for the task list
  // to refresh before clearing the loader so the new agent is visible first.
  const handleDuplicateAgent = async (agent) => {
    if (duplicatingId) return;
    setOpenDropdown(null);
    setDuplicatingId(agent.id);
    try {
      await duplicateTask(agent.id);
      if (isSuperAdmin) {
        await dispatch(fetchTasksAsync());
      } else {
        await dispatch(fetchTasksAsync(email));
      }
      addToast(t('agents.toastDuplicated', { name: agent.name }), 'success');
    } catch (err) {
      console.error('Duplicate agent failed:', err);
      addToast(t('agents.toastDuplicateError'), 'error');
    } finally {
      setDuplicatingId(null);
    }
  };

  // Normal users have no access to agent management — show nothing.
  if (!isSuperAdmin && !isAdmin) {
    return null;
  }

  return (
    <div className="h-screen bg-white overflow-hidden flex flex-col">
      <ToastContainer />

      {organizationsError && (
        <div className="px-10 py-2 text-[14px] text-red-600 bg-red-50">{organizationsError}</div>
      )}

      <header className="px-10 h-[80px] flex items-center justify-between flex-shrink-0">
        <h1 className="text-[22px] font-bold text-[#111827]">{t('agents.pageTitle')}</h1>
        <div className="flex items-center gap-3">
            <button
              onClick={handleCreateAgent}
              className="flex items-center gap-2 px-4 sm:px-5 py-2.5 rounded-lg text-white text-[14px] font-medium transition-colors hover:opacity-90"
              style={{ backgroundColor: aispaceColor }}
            >
              <Plus size={18} strokeWidth={2.5} />
              <span className="hidden sm:inline">{t('agents.createAgent')}</span>
              <span className="sm:hidden">{t('common.create')}</span>
            </button>
            {/* Delete All Button - shown when items are selected */}
            {selectedAgents.size > 0 && (
              <button
                onClick={handleBulkDeleteClick}
                className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-white text-[14px] font-medium bg-[#D32F2F] hover:bg-[#DC2626] transition-colors"
              >
                {t('history.deleteAll')}
              </button>
            )}
            <div className="relative" ref={headerDropdownRef}>
              <button
                onClick={() => setShowHeaderDropdown(!showHeaderDropdown)}
                className="p-2 text-[#6B7280] hover:text-[#374151] border border-[#EEEEEE] rounded-lg hover:bg-[#F9FAFB] transition-colors"
              >
                <MoreHorizontal size={20} />
              </button>

              {/* Header Dropdown Menu */}
              {showHeaderDropdown && (
                <div className="absolute right-0 top-full mt-2 bg-white border border-[#EEEEEE] rounded-lg shadow-lg z-50 overflow-hidden">
                  <button
                    onClick={toggleSelectAll}
                    className="flex items-center gap-3 px-4 py-3 text-[14px] text-[#374151] hover:bg-[#F9FAFB] transition-colors whitespace-nowrap"
                  >
                    {/* Checkbox icon */}
                    <div
                      className="w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors"
                      style={selectionModeActive ? { backgroundColor: aispaceColor, borderColor: aispaceColor } : { borderColor: '#D1D5DB' }}
                    >
                      {selectionModeActive && (
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3">
                          <path d="M20 6L9 17l-5-5"/>
                        </svg>
                      )}
                    </div>
                    <span>{t('agents.selectAll')}</span>
                  </button>
                </div>
              )}
            </div>
        </div>
      </header>

      <main className="px-4 sm:px-6 lg:px-10 pb-6 lg:pb-8 flex-1 flex flex-col overflow-hidden">
        {/* Card Container with border */}
        <div className="bg-white border border-[#EEEEEE] rounded-xl flex-1 overflow-hidden flex flex-col">
          {/* Search and Filters */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 px-4 sm:px-6 py-4 flex-shrink-0">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 w-full sm:w-auto">
              {/* Search Input */}
              <div className="relative w-full sm:w-[400px]">
                <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
                <input
                  type="text"
                  placeholder={t('common.search')}
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="w-full pl-10 pr-4 py-2.5 text-[14px] border border-[#EEEEEE] rounded-lg focus:outline-none focus:ring-0 transition-colors"
                  onFocus={(e) => e.target.style.borderColor = aispaceColor}
                  onBlur={(e) => e.target.style.borderColor = '#EEEEEE'}
                />
              </div>

              {/* Filters Button */}
              <button
                onClick={openFilterModal}
                className="flex items-center gap-2 px-4 py-2.5 text-[14px] text-[#374151] border border-[#EEEEEE] rounded-lg hover:bg-[#F9FAFB] transition-colors"
              >
                <Plus size={16} strokeWidth={2.5} />
                {t('history.filter')}
                {activeFilterCount > 0 && (
                  <span
                    className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[11px] font-semibold text-white rounded-full"
                    style={{ backgroundColor: aispaceColor }}
                  >
                    {activeFilterCount}
                  </span>
                )}
              </button>
            </div>

            {/* View Toggle */}
            <div className="flex items-center border border-[#EEEEEE] rounded-lg overflow-hidden">
              <button
                onClick={() => {
                  setViewMode('grid');
                  setCurrentPage(1);
                }}
                className={`p-2.5 transition-colors ${viewMode === 'grid' ? 'bg-[#F3F4F6] text-[#111827]' : 'text-[#6B7280] hover:bg-[#F9FAFB]'}`}
              >
                <LayoutGrid size={18} />
              </button>
              <button
                onClick={() => {
                  setViewMode('list');
                  setCurrentPage(1);
                }}
                className={`p-2.5 transition-colors ${viewMode === 'list' ? 'bg-[#F3F4F6] text-[#111827]' : 'text-[#6B7280] hover:bg-[#F9FAFB]'}`}
              >
                <List size={18} />
              </button>
            </div>
          </div>

          {/* Agents Content */}
          {status === 'loading' && tasks.length === 0 ? (
            <div className="px-6 py-12 text-center text-[#6B7280]">{t('agents.loading')}</div>
          ) : paginatedAgents.length === 0 ? (
            /* Empty State */
            <div className="flex-1 flex flex-col items-center justify-center py-16">
              {/* Decorative circle with + icon */}
              <div className="relative mb-6">
                {/* Outer decorative ring with dots */}
                <div className="w-[120px] h-[120px] rounded-full border-2 border-dashed border-[#EEEEEE] flex items-center justify-center relative">
                  {/* Decorative dots */}
                  <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-2 h-2 rounded-full bg-[#EEEEEE]"></div>
                  <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-2 h-2 rounded-full bg-[#EEEEEE]"></div>
                  <div className="absolute top-1/2 -left-1 -translate-y-1/2 w-2 h-2 rounded-full bg-[#EEEEEE]"></div>
                  <div className="absolute top-1/2 -right-1 -translate-y-1/2 w-2 h-2 rounded-full bg-[#EEEEEE]"></div>
                  {/* Inner circle with + icon */}
                  <div className="w-16 h-16 rounded-full bg-[#F9FAFB] border border-[#EEEEEE] flex items-center justify-center">
                    <Plus size={28} className="text-[#9CA3AF]" />
                  </div>
                </div>
              </div>
              {/* Text */}
              <h3 className="text-[16px] font-semibold text-[#111827] mb-2">
                {searchQuery ? t('agents.noneFound') : t('agents.noneYet')}
              </h3>
              <p className="text-[14px] text-[#6B7280] text-center max-w-md">
                {searchQuery
                  ? t('agents.noneFoundHint')
                  : t('agents.noneYetHint')}
              </p>
            </div>
          ) : viewMode === 'grid' ? (
            /* Grid View */
            <div className="flex-1 overflow-y-auto px-4 sm:px-6 pb-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
                {paginatedAgents.map((agent, index) => {
                  const itemsPerRow = 3;
                  const totalItems = paginatedAgents.length;
                  const lastRowStartIndex = Math.floor((totalItems - 1) / itemsPerRow) * itemsPerRow;
                  const isInLastRow = totalItems > itemsPerRow && index >= lastRowStartIndex;
                  const isDropdownOpen = openDropdown === agent.id;

                  return (
                    <div
                      key={agent.id}
                      className="bg-white rounded-xl border border-[#EEEEEE] hover:shadow-md transition-shadow relative"
                    >
                      {/* Checkbox on card - only show when selection mode is active */}
                      {selectionModeActive && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleAgentSelection(agent.id);
                          }}
                          className="absolute top-3 left-3 w-5 h-5 rounded border-2 flex items-center justify-center transition-colors z-20"
                          style={selectedAgents.has(agent.id) ? { backgroundColor: aispaceColor, borderColor: aispaceColor } : { backgroundColor: 'white', borderColor: '#D1D5DB' }}
                        >
                          {selectedAgents.has(agent.id) && (
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3">
                              <path d="M20 6L9 17l-5-5"/>
                            </svg>
                          )}
                        </button>
                      )}

                      {/* Action icons on banner - top right (outside overflow-hidden) */}
                      <div className="absolute top-3 right-3 flex items-center gap-2 z-20">
                        <button
                          onClick={() => handleSettingsClick(agent.id)}
                          className="p-2 bg-white text-[#6B7280] hover:text-[#374151] border border-[#EEEEEE] rounded-lg hover:bg-[#F9FAFB] transition-colors"
                        >
                          <Settings2 size={16} />
                        </button>
                        <div className="relative" ref={isDropdownOpen ? dropdownRef : null}>
                          <button
                            onClick={() => duplicatingId !== agent.id && setOpenDropdown(isDropdownOpen ? null : agent.id)}
                            disabled={duplicatingId === agent.id}
                            className="p-2 bg-white text-[#6B7280] hover:text-[#374151] border border-[#EEEEEE] rounded-lg hover:bg-[#F9FAFB] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                          >
                            {duplicatingId === agent.id ? (
                              <Loader2 size={16} className="animate-spin" style={{ color: aispaceColor }} />
                            ) : (
                              <MoreHorizontal size={16} />
                            )}
                          </button>

                          {/* Dropdown Menu */}
                          {isDropdownOpen && (
                            <div className={`absolute right-0 w-48 bg-white border border-[#EEEEEE] rounded-lg shadow-lg z-[999] py-1 ${isInLastRow ? 'bottom-full mb-2' : 'top-full mt-2'}`}>
                              {isSuperAdmin && (
                                <button
                                  onClick={() => handleDuplicateAgent(agent)}
                                  className="w-full flex items-center gap-3 px-4 py-2.5 text-[14px] text-[#374151] hover:bg-[#F9FAFB] transition-colors"
                                >
                                  <Copy size={16} className="text-[#6B7280]" />
                                  <span>{t('agents.duplicate')}</span>
                                </button>
                              )}
                              <button
                                onClick={() => handleDeleteClick(agent)}
                                className="w-full flex items-center gap-3 px-4 py-2.5 text-[14px] text-[#EF4444] hover:bg-[#FEF2F2] transition-colors"
                              >
                                <Trash2 size={16} />
                                <span>{t('common.delete')}</span>
                              </button>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Card Banner */}
                      <div className="overflow-hidden rounded-t-xl">
                        <AgentImage agentId={agent.id} agentName={agent.name} imagePath={agent.image_blob_path} type="banner" />
                      </div>

                      {/* Card Content */}
                      <div className="p-4">
                        {/* Agent Name */}
                        <div className="mb-3">
                          <p className="text-[12px] font-semibold text-[#374151] mb-1">{t('agents.agentNameLabel')}</p>
                          <p className="text-[14px] text-[#111827] truncate">{agent.name}</p>
                        </div>

                        {/* Department */}
                        <div className="mb-3">
                          <p className="text-[12px] font-semibold text-[#374151] mb-1">{t('agents.userGroupLabel')}</p>
                          <p className="text-[14px] text-[#111827]">{agent.department_name || '-'}</p>
                        </div>

                        {/* Organisation */}
                        <div className="mb-3">
                          <p className="text-[12px] font-semibold text-[#374151] mb-1">{t('agents.organizationLabel')}</p>
                          <p className="text-[14px] text-[#111827]">{getOrganizationName(agent.organization_id)}</p>
                        </div>

                        {/* Last Updated */}
                        <div>
                          <p className="text-[12px] font-semibold text-[#374151] mb-1">{t('agents.lastUpdatedLabel')}</p>
                          <p className="text-[14px] text-[#111827]">{formatTimestamp(agent.CreatedAt)}</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            /* List View */
            <div className="flex flex-col flex-1 overflow-hidden">
              {/* Table Container */}
              <div className="flex-1 overflow-y-auto">
                <div className="min-w-[800px]">
                  {/* Table Header */}
                  <div className="flex items-center px-4 sm:px-6 py-3 bg-[#F5F5F5] border-b border-[#EEEEEE] sticky top-0">
                    <div className="w-[280px] flex-shrink-0 flex items-center gap-3">
                      {/* Header Checkbox - only show when selection mode is active */}
                      {selectionModeActive && (
                        <button
                          onClick={toggleSelectAll}
                          className="w-5 h-5 rounded border-2 flex items-center justify-center transition-colors flex-shrink-0"
                          style={allCurrentPageSelected ? { backgroundColor: aispaceColor, borderColor: aispaceColor } : { borderColor: '#D1D5DB' }}
                        >
                          {allCurrentPageSelected && (
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3">
                              <path d="M20 6L9 17l-5-5"/>
                            </svg>
                          )}
                        </button>
                      )}
                      <span className="text-[13px] font-medium text-[#6B7280]">{t('agents.agentNameLabel')}</span>
                    </div>
                    <div className="w-[180px] flex-shrink-0 text-[13px] font-medium text-[#6B7280]">{t('agents.userGroupLabel')}</div>
                    <div className="flex-1 min-w-0 text-[13px] font-medium text-[#6B7280]">{t('agents.organizationLabel')}</div>
                    <div className="w-[140px] flex-shrink-0 text-[13px] font-medium text-[#6B7280]">{t('agents.lastUpdatedLabel')}</div>
                    <div className="w-[100px] flex-shrink-0"></div>
                  </div>

                  {/* Table Body */}
                  <div>
                    {paginatedAgents.map((agent, index) => {
                      const isLastItem = paginatedAgents.length > 3 && index === paginatedAgents.length - 1;
                      const isDropdownOpen = openDropdown === agent.id;

                      return (
                        <div
                          key={agent.id}
                          className={`flex items-center px-4 sm:px-6 py-4 hover:bg-[#FAFAFA] transition-colors ${
                            index !== paginatedAgents.length - 1 ? 'border-b border-[#EEEEEE]' : ''
                          }`}
                        >
                          {/* Agent Name with Icon and Checkbox */}
                          <div className="w-[280px] flex-shrink-0 flex items-center gap-3 min-w-0">
                            {/* Row Checkbox - only show when selection mode is active */}
                            {selectionModeActive && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleAgentSelection(agent.id);
                                }}
                                className="w-5 h-5 rounded border-2 flex items-center justify-center transition-colors flex-shrink-0"
                                style={selectedAgents.has(agent.id) ? { backgroundColor: aispaceColor, borderColor: aispaceColor } : { borderColor: '#D1D5DB' }}
                              >
                                {selectedAgents.has(agent.id) && (
                                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3">
                                    <path d="M20 6L9 17l-5-5"/>
                                  </svg>
                                )}
                              </button>
                            )}
                            <AgentImage agentId={agent.id} agentName={agent.name} imagePath={agent.image_blob_path} type="icon" />
                            <span className="text-[14px] font-medium text-[#111827] truncate">{agent.name}</span>
                          </div>

                          {/* Department */}
                          <div className="w-[180px] flex-shrink-0 text-[14px] text-[#6B7280] truncate">
                            {agent.department_name || '-'}
                          </div>

                          {/* Organisation */}
                          <div className="flex-1 min-w-0 text-[14px] text-[#374151] truncate">
                            {getOrganizationName(agent.organization_id)}
                          </div>

                          {/* Last Updated */}
                          <div className="w-[140px] flex-shrink-0 text-[14px] text-[#6B7280]">
                            {formatTimestamp(agent.CreatedAt)}
                          </div>

                          {/* Actions */}
                          <div className="w-[100px] flex-shrink-0 flex items-center justify-end gap-2">
                            <button
                              onClick={() => handleSettingsClick(agent.id)}
                              className="p-2 text-[#9CA3AF] hover:text-[#6B7280] border border-[#EEEEEE] rounded-lg hover:bg-[#F9FAFB] transition-colors"
                              title="Einstellungen"
                            >
                              <Settings2 size={18} />
                            </button>
                            <div className="relative" ref={isDropdownOpen ? dropdownRef : null}>
                              <button
                                onClick={() => duplicatingId !== agent.id && setOpenDropdown(isDropdownOpen ? null : agent.id)}
                                disabled={duplicatingId === agent.id}
                                className="p-2 text-[#9CA3AF] hover:text-[#6B7280] border border-[#EEEEEE] rounded-lg hover:bg-[#F9FAFB] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                              >
                                {duplicatingId === agent.id ? (
                                  <Loader2 size={18} className="animate-spin" style={{ color: aispaceColor }} />
                                ) : (
                                  <MoreHorizontal size={18} />
                                )}
                              </button>

                              {/* Dropdown Menu */}
                              {isDropdownOpen && (
                                <div
                                  className={`absolute right-0 ${isLastItem ? 'bottom-full mb-2' : 'top-full mt-2'} w-48 bg-white border border-[#EEEEEE] rounded-lg shadow-xl z-[999] py-1`}
                                >
                                  {isSuperAdmin && (
                                    <button
                                      onClick={() => handleDuplicateAgent(agent)}
                                      className="w-full flex items-center gap-3 px-4 py-2.5 text-[14px] text-[#374151] hover:bg-[#F9FAFB] transition-colors"
                                    >
                                      <Copy size={16} className="text-[#6B7280]" />
                                      <span>{t('agents.duplicate')}</span>
                                    </button>
                                  )}
                                  <button
                                    onClick={() => handleDeleteClick(agent)}
                                    className="w-full flex items-center gap-3 px-4 py-2.5 text-[14px] text-[#EF4444] hover:bg-[#FEF2F2] transition-colors"
                                  >
                                    <Trash2 size={16} />
                                    <span>{t('common.delete')}</span>
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Pagination - Inside the card, hidden when no agents */}
          {filteredAgents.length > 0 && (
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-4 sm:px-6 py-4 border-t border-[#EEEEEE] flex-shrink-0">
              <span className="text-[14px] text-[#6B7280]">
                {t('pagination.pageOf', { current: currentPage, total: totalPages || 1 })}
              </span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  className="px-4 py-2 text-[14px] font-medium text-[#374151] border border-[#EEEEEE] rounded-lg hover:bg-[#F9FAFB] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {t('common.back')}
                </button>
                <button
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages || totalPages === 0}
                  className="px-4 py-2 text-[14px] font-medium text-[#374151] border border-[#EEEEEE] rounded-lg hover:bg-[#F9FAFB] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {t('common.next')}
                </button>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Delete Confirmation Modal */}
      {deleteModal.isOpen && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/30"
            onClick={cancelDelete}
          />

          {/* Modal */}
          <div className="relative bg-white rounded-xl shadow-xl w-[400px] p-6 mx-4">
            {/* Warning Icon */}
            <div className="flex justify-center mb-4">
              <div className="w-12 h-12 rounded-full bg-[#FEE2E2] flex items-center justify-center">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="12" cy="12" r="10" stroke="#EF4444" strokeWidth="2"/>
                  <path d="M12 8V12" stroke="#EF4444" strokeWidth="2" strokeLinecap="round"/>
                  <circle cx="12" cy="16" r="1" fill="#EF4444"/>
                </svg>
              </div>
            </div>

            {/* Title */}
            <h3 className="text-[18px] font-semibold text-[#111827] text-center mb-2">
              {deleteModal.isBulk && selectedAgents.size > 1 ? t('agents.deleteTitleBulk', { count: selectedAgents.size }) : t('agents.deleteTitle')}
            </h3>

            {/* Description */}
            <p className="text-[14px] text-[#6B7280] text-center mb-6">
              {deleteModal.isBulk
                ? `Sind Sie sicher, dass Sie ${selectedAgents.size} ausgewählte${selectedAgents.size > 1 ? '' : 'n'} Agent${selectedAgents.size > 1 ? 'en' : ''} löschen möchten? Diese Aktion kann nicht rückgängig gemacht werden.`
                : `Sind Sie sicher, dass Sie "${deleteModal.agent?.name}" löschen möchten? Diese Aktion kann nicht rückgängig gemacht werden.`
              }
            </p>

            {/* Buttons */}
            <div className="flex gap-3">
              <button
                onClick={cancelDelete}
                className="flex-1 px-4 py-2.5 text-[14px] font-medium text-[#374151] bg-white border border-[#D1D5DB] rounded-lg hover:bg-[#F9FAFB] transition-colors"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={deleteModal.isBulk ? confirmBulkDelete : confirmDelete}
                disabled={deletingId}
                className="flex-1 px-4 py-2.5 text-[14px] font-medium text-white bg-[#EF4444] rounded-lg hover:bg-[#DC2626] transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {deletingId ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    {t('deleteModal.deleting')}
                  </>
                ) : (
                  deleteModal.isBulk ? t('history.deleteAll') : t('common.delete')
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Agent Modal */}
      {createAgentModal && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/30"
            onClick={isCreatingAgent ? undefined : closeCreateAgentModal}
          />

          {/* Modal */}
          <div className="relative bg-white rounded-xl shadow-xl w-[480px] max-h-[90vh] mx-4 flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-5 border-b border-[#E5E7EB]">
              <h2 className="text-[20px] font-semibold text-[#111827]">{t('agents.createModalTitle')}</h2>
              <button
                onClick={closeCreateAgentModal}
                disabled={isCreatingAgent}
                className="p-1 hover:bg-[#F3F4F6] rounded-lg transition-colors disabled:opacity-50"
              >
                <X size={20} className="text-[#6B7280]" />
              </button>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto px-6 py-6 space-y-6">
              {/* Agent Image */}
              <div>
                <label className="block text-[14px] font-medium text-[#111827] mb-3">{t('agents.createModalImageLabel')}</label>
                <div className="flex items-center gap-4">
                  {/* Preview */}
                  <div className="w-[72px] h-[72px] rounded-full bg-gradient-to-br from-[#E0E7FF] via-[#C7D2FE] to-[#A5B4FC] flex items-center justify-center overflow-hidden flex-shrink-0">
                    {newAgent.image ? (
                      <img src={newAgent.image} alt={t('agentSettings.agentImageLabel')} className="w-full h-full object-cover" />
                    ) : (
                      <svg className="w-10 h-10 opacity-80" viewBox="0 0 40 40" fill="none">
                        <path d="M10 28C15 23 20 18 25 18C30 18 35 23 40 20" stroke="#818CF8" strokeWidth="2" strokeLinecap="round" fill="none"/>
                        <path d="M8 30C13 25 18 20 23 20C28 20 33 25 38 22" stroke="#6366F1" strokeWidth="1.5" strokeLinecap="round" fill="none" opacity="0.7"/>
                        <path d="M25 12V24" stroke="#22C55E" strokeWidth="1.5" strokeLinecap="round" opacity="0.8"/>
                      </svg>
                    )}
                  </div>

                  {/* Upload Button */}
                  <input
                    type="file"
                    ref={agentImageInputRef}
                    onChange={handleAgentImageUpload}
                    accept="image/*"
                    className="hidden"
                  />
                  <button
                    onClick={() => agentImageInputRef.current?.click()}
                    className="px-5 py-2.5 text-[14px] font-medium text-white rounded-lg transition-colors hover:opacity-90"
                    style={{ backgroundColor: aispaceColor }}
                  >
                    {newAgent.image ? t('agents.createModalChangeImage') : t('agents.createModalUploadImage')}
                  </button>

                  {newAgent.image ? (
                    <button
                      onClick={() => setNewAgent(prev => ({ ...prev, image: null }))}
                      className="px-5 py-2.5 text-[14px] font-medium text-[#EF4444] bg-white border border-[#EF4444] rounded-lg hover:bg-[#FEF2F2] transition-colors"
                    >
                      {t('agents.createModalRemoveImage')}
                    </button>
                  ) : (
                    <span className="text-[14px] text-[#9CA3AF]">
                      {t('agents.createModalNoFileSelected')}
                    </span>
                  )}
                </div>
              </div>

              {/* Agent Name */}
              <div>
                <label className="block text-[14px] font-medium text-[#111827] mb-2">{t('agents.agentNameLabel')}</label>
                <input
                  type="text"
                  value={newAgent.name}
                  onChange={(e) => setNewAgent(prev => ({ ...prev, name: e.target.value }))}
                  placeholder={t('agents.agentNameLabel')}
                  className="w-full px-4 py-3 text-[14px] border border-[#E5E7EB] rounded-lg focus:outline-none transition-colors"
                  onFocus={(e) => e.target.style.borderColor = aispaceColor}
                  onBlur={(e) => e.target.style.borderColor = '#E5E7EB'}
                />
              </div>

              {/* Organization Dropdown */}
              <div>
                <label className="block text-[14px] font-medium text-[#111827] mb-2">{t('agents.organizationLabel')}</label>
                <div className="relative" ref={orgDropdownRef}>
                  <button
                    type="button"
                    onClick={() => { setOrgDropdownOpen(!orgDropdownOpen); setDeptDropdownOpen(false); }}
                    className="w-full px-4 py-3 pr-10 text-[14px] text-left border rounded-lg bg-white focus:outline-none cursor-pointer"
                    style={{ borderColor: orgDropdownOpen ? aispaceColor : '#E5E7EB' }}
                  >
                    <span className={newAgent.organization_id ? 'text-[#111827]' : 'text-[#9CA3AF]'}>
                      {newAgent.organization_id
                        ? organizations.find(o => o.organization_id === newAgent.organization_id)?.organization_name
                        : t('agents.createModalSelectOrg')}
                    </span>
                  </button>
                  <ChevronDown size={18} className={`absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-[#6B7280] transition-transform ${orgDropdownOpen ? 'rotate-180' : ''}`} />
                  {orgDropdownOpen && (
                    <div className="absolute z-50 mt-1 w-full bg-white border border-[#E5E7EB] rounded-lg shadow-lg max-h-[200px] overflow-y-auto">
                      <div
                        onClick={() => {
                          setNewAgent(prev => ({ ...prev, organization_id: '', department_id: '' }));
                          setOrgDepartments([]);
                          setOrgDropdownOpen(false);
                        }}
                        className="px-4 py-2.5 text-[14px] text-[#9CA3AF] hover:bg-[#F9FAFB] cursor-pointer"
                      >
                        {t('agents.createModalSelectOrg')}
                      </div>
                      {creatableOrganizations.map(org => (
                        <div
                          key={org.organization_id}
                          onClick={async () => {
                            setNewAgent(prev => ({ ...prev, organization_id: org.organization_id, department_id: '' }));
                            setOrgDropdownOpen(false);
                            try {
                              const res = await axios.get(`${BASE_URL}/departments/${org.organization_id}`, {
                                headers: { ...authHeader() },
                              });
                              setOrgDepartments(res.data.departments || []);
                            } catch { setOrgDepartments([]); }
                          }}
                          className={`px-4 py-2.5 text-[14px] cursor-pointer transition-colors ${
                            newAgent.organization_id === org.organization_id
                              ? 'font-medium'
                              : 'text-[#374151] hover:bg-[#F9FAFB]'
                          }`}
                          style={newAgent.organization_id === org.organization_id ? { backgroundColor: `${aispaceColor}10`, color: aispaceColor } : {}}
                        >
                          {org.organization_name}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Department Dropdown */}
              <div>
                <label className="block text-[14px] font-medium text-[#111827] mb-2">{t('agents.userGroupLabel')}</label>
                <div className="relative" ref={deptDropdownRef}>
                  <button
                    type="button"
                    onClick={() => { if (newAgent.organization_id) { setDeptDropdownOpen(!deptDropdownOpen); setOrgDropdownOpen(false); } }}
                    disabled={!newAgent.organization_id}
                    className="w-full px-4 py-3 pr-10 text-[14px] text-left border rounded-lg bg-white focus:outline-none cursor-pointer disabled:bg-[#F9FAFB] disabled:cursor-not-allowed"
                    style={{ borderColor: deptDropdownOpen ? aispaceColor : '#E5E7EB' }}
                  >
                    <span className={newAgent.department_id ? 'text-[#111827]' : 'text-[#9CA3AF]'}>
                      {newAgent.department_id
                        ? orgDepartments.find(d => String(d.id) === String(newAgent.department_id))?.department_name
                        : t('agents.createModalSelectDept')}
                    </span>
                  </button>
                  <ChevronDown size={18} className={`absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-[#6B7280] transition-transform ${deptDropdownOpen ? 'rotate-180' : ''}`} />
                  {deptDropdownOpen && (
                    <div className="absolute z-50 mt-1 w-full bg-white border border-[#E5E7EB] rounded-lg shadow-lg max-h-[200px] overflow-y-auto">
                      <div
                        onClick={() => {
                          setNewAgent(prev => ({ ...prev, department_id: '' }));
                          setDeptDropdownOpen(false);
                        }}
                        className="px-4 py-2.5 text-[14px] text-[#9CA3AF] hover:bg-[#F9FAFB] cursor-pointer"
                      >
                        {t('agents.createModalSelectDept')}
                      </div>
                      {orgDepartments.map(dept => (
                        <div
                          key={dept.id}
                          onClick={() => {
                            setNewAgent(prev => ({ ...prev, department_id: String(dept.id) }));
                            setDeptDropdownOpen(false);
                          }}
                          className={`px-4 py-2.5 text-[14px] cursor-pointer transition-colors ${
                            String(newAgent.department_id) === String(dept.id)
                              ? 'font-medium'
                              : 'text-[#374151] hover:bg-[#F9FAFB]'
                          }`}
                          style={String(newAgent.department_id) === String(dept.id) ? { backgroundColor: `${aispaceColor}10`, color: aispaceColor } : {}}
                        >
                          {dept.department_name}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="block text-[14px] font-medium text-[#111827] mb-2">{t('agents.createModalDescLabel')}</label>
                <textarea
                  value={newAgent.description}
                  onChange={(e) => setNewAgent(prev => ({ ...prev, description: e.target.value }))}
                  placeholder={t('agents.createModalDescPlaceholder')}
                  rows={4}
                  className="w-full px-4 py-3 text-[14px] border border-[#E5E7EB] rounded-lg focus:outline-none focus:ring-0 resize-none"
                  onFocus={(e) => e.target.style.borderColor = aispaceColor}
                  onBlur={(e) => e.target.style.borderColor = '#E5E7EB'}
                />
                <p className="text-[13px] text-[#9CA3AF] mt-1.5">
                  {t('agents.createModalDescHelp')}
                </p>
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-[#E5E7EB]">
              <button
                onClick={closeCreateAgentModal}
                disabled={isCreatingAgent}
                className="px-6 py-2.5 text-[14px] font-medium text-[#374151] bg-white border border-[#D1D5DB] rounded-lg hover:bg-[#F9FAFB] transition-colors disabled:opacity-50"
              >
                {t('agents.createModalCancel')}
              </button>
              <button
                onClick={handleAddAgent}
                disabled={!newAgent.name.trim() || isCreatingAgent}
                className="px-6 py-2.5 text-[14px] font-medium text-white rounded-lg transition-colors hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                style={{ backgroundColor: aispaceColor }}
              >
                {isCreatingAgent ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    {t('agents.createModalSubmitting')}
                  </>
                ) : (
                  t('agents.createModalSubmit')
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Agent Filter Modal */}
      {filterOpen && (() => {
        const months = [
          { v: '0', l: t('months.january') }, { v: '1', l: t('months.february') }, { v: '2', l: t('months.march') }, { v: '3', l: t('months.april') },
          { v: '4', l: t('months.may') }, { v: '5', l: t('months.june') }, { v: '6', l: t('months.july') }, { v: '7', l: t('months.august') },
          { v: '8', l: t('months.september') }, { v: '9', l: t('months.october') }, { v: '10', l: t('months.november') }, { v: '11', l: t('months.december') },
        ];
        const sortOpts = [
          { v: 'newest', l: t('filterModal.newestFirst') },
          { v: 'oldest', l: t('filterModal.oldestFirst') },
          { v: 'mostProgress', l: t('filterModal.mostProgress') },
          { v: 'leastProgress', l: t('filterModal.leastProgress') },
        ];
        const selStyle = (active) => active
          ? { backgroundColor: `${aispaceColor}1A`, borderColor: aispaceColor, color: aispaceColor }
          : { borderColor: '#EEEEEE', color: '#111827', backgroundColor: 'white' };

        return (
          <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-[200]" onClick={() => setFilterOpen(false)}>
            <div className="bg-white rounded-2xl w-[600px] max-h-[90vh] shadow-xl flex flex-col" onClick={(e) => e.stopPropagation()}>
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-5 border-b border-[#EEEEEE] rounded-t-2xl">
                <h2 className="text-[20px] font-bold text-[#111827]">{t('filterModal.title')}</h2>
                <button onClick={() => setFilterOpen(false)} className="p-1 hover:bg-[#F3F4F6] rounded-lg transition-colors">
                  <X size={20} className="text-[#6B7280]" />
                </button>
              </div>

              {/* Body */}
              <div className="px-6 py-5 space-y-6 overflow-y-auto flex-1">
                {/* Organisation multi-select — applied first; selecting orgs
                    drives the User Group options below via orgDeptOptions. */}
                <div>
                  <p className="text-[14px] font-semibold text-[#111827] mb-2">{t('filterModal.organizationLabel')}</p>
                  <div className="relative" ref={orgDdRef}>
                    <button
                      type="button"
                      onClick={() => { setOrgDdOpen(v => !v); setUgDdOpen(false); setMonthDdOpen(false); setSortDdOpen(false); }}
                      className="w-full flex items-center justify-between border border-[#EEEEEE] rounded-xl px-4 py-3 bg-white hover:bg-[#F9FAFB] focus:outline-none min-h-[48px] flex-wrap gap-1"
                      style={orgDdOpen ? { borderColor: aispaceColor } : {}}
                    >
                      <div className="flex flex-wrap gap-1 flex-1 text-left">
                        {draftOrgs.length === 0 && (
                          <span className="text-[14px] text-[#9CA3AF]">{t('filterModal.organizationPlaceholder')}</span>
                        )}
                        {draftOrgs.map((orgId) => {
                          const o = organizations.find((x) => x.organization_id === orgId);
                          const label = o ? o.organization_name : orgId;
                          return (
                            <span key={orgId} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[12px] font-medium bg-[#F3F4F6] text-[#111827]">
                              {label}
                              <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); setDraftOrgs(prev => prev.filter(x => x !== orgId)); }}
                                className="hover:text-red-500"
                              >
                                <X size={12} />
                              </button>
                            </span>
                          );
                        })}
                      </div>
                      <ChevronDown size={16} className={`text-[#6B7280] flex-shrink-0 transition-transform ${orgDdOpen ? 'rotate-180' : ''}`} />
                    </button>
                    {orgDdOpen && (
                      <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-[#EEEEEE] rounded-lg shadow-lg z-30 max-h-[200px] overflow-y-auto py-1">
                        {creatableOrganizations.length === 0 ? (
                          <div className="px-4 py-2.5 text-[14px] text-[#9CA3AF]">{t('filterModal.organizationPlaceholder')}</div>
                        ) : (
                          creatableOrganizations.map((o) => {
                            const sel = draftOrgs.includes(o.organization_id);
                            return (
                              <button
                                key={o.organization_id}
                                type="button"
                                onClick={() => setDraftOrgs(prev => sel ? prev.filter(x => x !== o.organization_id) : [...prev, o.organization_id])}
                                className={`w-full px-4 py-2.5 flex items-center justify-between text-left text-[14px] text-[#111827] hover:bg-[#F9FAFB] ${sel ? 'bg-[#F3F4F6]' : ''}`}
                              >
                                <span className="truncate">{o.organization_name}</span>
                                {sel && <Check size={16} style={{ color: aispaceColor }} />}
                              </button>
                            );
                          })
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* User Group multi-select — options depend on the selected Organisations above */}
                <div>
                  <p className="text-[14px] font-semibold text-[#111827] mb-2">{t('filterModal.userGroupLabel')}</p>
                  <div className="relative" ref={ugDdRef}>
                    <button
                      type="button"
                      onClick={() => { setUgDdOpen(v => !v); setOrgDdOpen(false); setMonthDdOpen(false); setSortDdOpen(false); }}
                      className="w-full flex items-center justify-between border border-[#EEEEEE] rounded-xl px-4 py-3 bg-white hover:bg-[#F9FAFB] focus:outline-none min-h-[48px] flex-wrap gap-1"
                      style={ugDdOpen ? { borderColor: aispaceColor } : {}}
                    >
                      <div className="flex flex-wrap gap-1 flex-1 text-left">
                        {draftUserGroups.length === 0 && (
                          <span className="text-[14px] text-[#9CA3AF]">{t('filterModal.userGroupPlaceholder')}</span>
                        )}
                        {draftUserGroups.map(name => (
                          <span key={name} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[12px] font-medium bg-[#F3F4F6] text-[#111827]">
                            {name}
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); setDraftUserGroups(prev => prev.filter(x => x !== name)); }}
                              className="hover:text-red-500"
                            >
                              <X size={12} />
                            </button>
                          </span>
                        ))}
                      </div>
                      <ChevronDown size={16} className={`text-[#6B7280] flex-shrink-0 transition-transform ${ugDdOpen ? 'rotate-180' : ''}`} />
                    </button>
                    {ugDdOpen && (() => {
                      // When orgs are selected, only show their departments;
                      // ADMIN 2: admin → all departments of their org.
                      // Super-Admin → all departments across all orgs.
                      // Normal users → task-derived list.
                      const activeOpts = draftOrgs.length > 0
                        ? orgDeptOptions
                        : ((isAdmin || isSuperAdmin) && allOrgDeptNames.length > 0
                            ? allOrgDeptNames
                            : availableUserGroups);
                      return (
                        <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-[#EEEEEE] rounded-lg shadow-lg z-30 max-h-[200px] overflow-y-auto py-1">
                          {activeOpts.length === 0 ? (
                            <div className="px-4 py-2.5 text-[14px] text-[#9CA3AF]">{t('filterModal.userGroupPlaceholder')}</div>
                          ) : (
                            activeOpts.map(name => {
                              const sel = draftUserGroups.includes(name);
                              return (
                                <button
                                  key={name}
                                  type="button"
                                  onClick={() => setDraftUserGroups(prev => sel ? prev.filter(x => x !== name) : [...prev, name])}
                                  className={`w-full px-4 py-2.5 flex items-center justify-between text-left text-[14px] text-[#111827] hover:bg-[#F9FAFB] ${sel ? 'bg-[#F3F4F6]' : ''}`}
                                >
                                  <span className="truncate">{name}</span>
                                  {sel && <Check size={16} style={{ color: aispaceColor }} />}
                                </button>
                              );
                            })
                          )}
                        </div>
                      );
                    })()}
                  </div>
                </div>

                {/* Last Update */}
                <div>
                  <p className="text-[14px] font-semibold text-[#111827] mb-2">{t('filterModal.lastUpdateLabel')}</p>
                  <div className="grid grid-cols-3 gap-3 mb-3">
                    {[
                      { v: 'today', l: t('filterModal.today') },
                      { v: '7days', l: t('filterModal.last7days') },
                      { v: '30days', l: t('filterModal.last30days') },
                    ].map(o => (
                      <button
                        key={o.v}
                        type="button"
                        onClick={() => setDraftDate(draftDate === o.v ? null : o.v)}
                        className="px-4 py-3 rounded-xl border text-[14px] font-semibold transition-colors"
                        style={selStyle(draftDate === o.v)}
                      >
                        {o.l}
                      </button>
                    ))}
                  </div>
                  {/* Month dropdown */}
                  <div className="relative" ref={monthDdRef}>
                    <button
                      type="button"
                      onClick={() => { setMonthDdOpen(v => !v); setOrgDdOpen(false); setUgDdOpen(false); setSortDdOpen(false); }}
                      className="w-full flex items-center justify-between border border-[#EEEEEE] rounded-xl px-4 py-3 bg-white hover:bg-[#F9FAFB] focus:outline-none text-[14px]"
                      style={monthDdOpen ? { borderColor: aispaceColor } : {}}
                    >
                      <span className={draftMonth ? 'text-[#111827]' : 'text-[#9CA3AF]'}>
                        {draftMonth ? months.find(m => m.v === draftMonth)?.l : t('filterModal.monthPlaceholder')}
                      </span>
                      <ChevronDown size={16} className={`text-[#6B7280] transition-transform ${monthDdOpen ? 'rotate-180' : ''}`} />
                    </button>
                    {monthDdOpen && (
                      <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-[#EEEEEE] rounded-lg shadow-lg z-30 max-h-[200px] overflow-y-auto py-1">
                        {months.map(m => (
                          <button
                            key={m.v}
                            type="button"
                            onClick={() => { setDraftMonth(draftMonth === m.v ? '' : m.v); setMonthDdOpen(false); }}
                            className={`w-full px-4 py-2.5 flex items-center justify-between text-left text-[14px] text-[#111827] hover:bg-[#F9FAFB] ${draftMonth === m.v ? 'bg-[#F3F4F6]' : ''}`}
                          >
                            <span>{m.l}</span>
                            {draftMonth === m.v && <Check size={16} style={{ color: aispaceColor }} />}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Sort By */}
                <div>
                  <p className="text-[14px] font-semibold text-[#111827] mb-2">{t('filterModal.sortByLabel')}</p>
                  <div className="relative" ref={sortDdRef}>
                    <button
                      type="button"
                      onClick={() => { setSortDdOpen(v => !v); setOrgDdOpen(false); setUgDdOpen(false); setMonthDdOpen(false); }}
                      className="w-full flex items-center justify-between border border-[#EEEEEE] rounded-xl px-4 py-3 bg-white hover:bg-[#F9FAFB] focus:outline-none text-[14px]"
                      style={sortDdOpen ? { borderColor: aispaceColor } : {}}
                    >
                      <span className="text-[#111827]">{sortOpts.find(s => s.v === draftSort)?.l}</span>
                      <ChevronDown size={16} className={`text-[#6B7280] transition-transform ${sortDdOpen ? 'rotate-180' : ''}`} />
                    </button>
                    {sortDdOpen && (
                      <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-[#EEEEEE] rounded-lg shadow-lg z-30 py-1">
                        {sortOpts.map(s => (
                          <button
                            key={s.v}
                            type="button"
                            onClick={() => { setDraftSort(s.v); setSortDdOpen(false); }}
                            className={`w-full px-4 py-2.5 flex items-center justify-between text-left text-[14px] text-[#111827] hover:bg-[#F9FAFB] ${draftSort === s.v ? 'bg-[#F3F4F6]' : ''}`}
                          >
                            <span>{s.l}</span>
                            {draftSort === s.v && <Check size={16} style={{ color: aispaceColor }} />}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="flex items-center justify-end gap-3 px-6 py-4 bg-[#F9FAFB] border-t border-[#EEEEEE] rounded-b-2xl">
                <button
                  onClick={clearAllFilters}
                  className="px-5 py-2.5 text-[14px] font-medium text-[#374151] bg-white border border-[#EEEEEE] rounded-lg hover:bg-[#F3F4F6] transition-colors"
                >
                  {t('filterModal.clearAll')}
                </button>
                <button
                  onClick={applyFilters}
                  className="px-6 py-2.5 text-[14px] font-semibold text-white rounded-lg hover:opacity-90 transition-colors"
                  style={{ backgroundColor: aispaceColor }}
                >
                  {t('filterModal.apply')}
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default Agents;
